import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import Papa from "papaparse";
import {
  ExternalLink,
  Plus,
  Upload,
  MoreHorizontal,
  Search,
  Check,
  Download,
  X,
  Trash2,
  Pencil,
} from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import { isIdInEntryRange, looksLikeCreativeId } from "@/lib/creative-id";

const ACCENT = "#7c3aed";

const CATEGORIES = [
  "Kitchen",
  "Home Improvement",
  "Automotive",
  "Personal Care",
  "Automotive Care",
  "Jewelry",
  "Pet",
  "Shoes",
  "Decor",
  "Gardening",
  "Multi Purpose",
  "Other",
];

const TYPES = ["asmr", "script", "music"] as const;
const TYPE_LABEL: Record<string, string> = { asmr: "ASMR", script: "Script", music: "Music" };

const NATURES = ["new", "variations", "hooks", "cta", "body", "others"] as const;
const NATURE_LABEL: Record<string, string> = {
  new: "New",
  variations: "Variations",
  hooks: "Hooks",
  cta: "CTA",
  body: "Body",
  others: "Others",
};
const NATURE_PILL: Record<string, string> = {
  new: "bg-emerald-100 text-emerald-700 border-emerald-200",
  variations: "bg-blue-100 text-blue-700 border-blue-200",
  hooks: "bg-orange-100 text-orange-700 border-orange-200",
  cta: "bg-purple-100 text-purple-700 border-purple-200",
  body: "bg-gray-100 text-gray-700 border-gray-200",
  others: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const ALLOWED_DEPTS = new Set(["editor", "marketer", "scriptwriter"]);

export const Route = createFileRoute("/_app/creative-library")({
  component: CreativeLibraryPage,
});

interface Entry {
  id: string;
  user_id: string;
  entry_date: string;
  product_code: string;
  product_name: string;
  category: string;
  raw_videos_link: string | null;
  types: string[];
  script_link: string | null;
  features_link: string | null;
  creative_link: string;
  creative_id_from: string | null;
  creative_id_to: string | null;
  creative_nature: string;
  notes: string | null;
  created_at: string;
}

interface ProductSearchResult {
  product_code: string;
  product_name: string;
  category: string;
  raw_videos_link: string | null;
  features_link: string | null;
}

function todayIST(): string {
  // YYYY-MM-DD in IST
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
  return ist.toISOString().slice(0, 10);
}

function todayISTDisplay(): string {
  const now = new Date();
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(now);
}

function CreativeLibraryPage() {
  const { profile, role } = useAuth();
  const allowed =
    role === "admin" || role === "moderator" || (profile && ALLOWED_DEPTS.has(profile.department));
  if (profile && !allowed) return <Navigate to="/dashboard" />;

  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterProduct, setFilterProduct] = useState<string>("all");
  const [openEntry, setOpenEntry] = useState(false);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [openImport, setOpenImport] = useState(false);

  const entriesQ = useQuery({
    queryKey: ["creative-library-entries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creative_library_entries" as never)
        .select("*")
        .order("entry_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Entry[];
    },
  });

  const entries = entriesQ.data ?? [];

  const productOptions = useMemo(() => {
    const set = new Map<string, string>();
    entries.forEach((e) => set.set(e.product_code, e.product_name));
    return Array.from(set.entries());
  }, [entries]);

  const isIdQuery = looksLikeCreativeId(search);

  const rangeMatches = useMemo(() => {
    if (!isIdQuery) return [] as Entry[];
    return entries.filter((e) => isIdInEntryRange(search, e.creative_id_from, e.creative_id_to));
  }, [entries, search, isIdQuery]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (q && !e.product_code.toLowerCase().includes(q) && !e.product_name.toLowerCase().includes(q))
        return false;
      if (filterCategory !== "all" && e.category !== filterCategory) return false;
      if (filterType !== "all" && !(e.types ?? []).includes(filterType)) return false;
      if (filterProduct !== "all" && e.product_code !== filterProduct) return false;
      return true;
    });
  }, [entries, search, filterCategory, filterType, filterProduct]);


  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("creative_library_entries" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Entry deleted");
      qc.invalidateQueries({ queryKey: ["creative-library-entries"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader subtitle="All product creative entries" />
      <div className="flex-1 p-4 md:p-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold">Creative Library</h1>
          <p className="text-sm text-muted-foreground">All product creative entries</p>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, ID, or range (e.g. emzz.546)"
                className="pl-8"
              />
              {isIdQuery && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Searching by creative ID — will match ranges automatically
                </p>
              )}
            </div>

            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {productOptions.map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {code} — {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setOpenImport(true)}>
              <Upload className="mr-1.5 h-4 w-4" /> Import
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setOpenEntry(true);
              }}
              style={{ backgroundColor: ACCENT, color: "white" }}
              className="hover:opacity-90"
            >
              <Plus className="mr-1.5 h-4 w-4" /> New Entry
            </Button>
          </div>
        </div>

        {isIdQuery && rangeMatches.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <div className="mb-2 text-[12px] font-semibold text-amber-900">
              {rangeMatches.length === 1
                ? `1 entry matches range for "${search.trim()}"`
                : `Found ${rangeMatches.length} entries matching range for "${search.trim()}"`}
            </div>
            <div className="flex flex-col gap-2">
              {rangeMatches.map((e) => (
                <div
                  key={`range-${e.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                        Range match
                      </span>
                      <span className="text-[13px] font-semibold">
                        {e.product_code} · {e.product_name}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      Range: {e.creative_id_from ?? "—"} → {e.creative_id_to ?? "—"} · {e.category}
                    </div>
                  </div>
                  {e.creative_link && (
                    <Button
                      size="sm"
                      onClick={() => window.open(e.creative_link, "_blank", "noopener,noreferrer")}
                    >
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open Drive Link
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-card">

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">S.NO</TableHead>
                <TableHead>DATE</TableHead>
                <TableHead>PRODUCT CODE</TableHead>
                <TableHead>PRODUCT NAME</TableHead>
                <TableHead>CATEGORY</TableHead>
                <TableHead>TYPE</TableHead>
                <TableHead>CREATIVE ID RANGE</TableHead>
                <TableHead>NATURE</TableHead>
                <TableHead>CREATIVE LINK</TableHead>
                <TableHead>SCRIPT LINK</TableHead>
                <TableHead>STATUS</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entriesQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!entriesQ.isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                    No entries yet. Click "+ New Entry" to add the first one.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((e, idx) => {
                const range =
                  e.creative_id_from && e.creative_id_to
                    ? `${e.creative_id_from} → ${e.creative_id_to}`
                    : e.creative_id_from || e.creative_id_to || "—";
                return (
                  <TableRow key={e.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell className="whitespace-nowrap">{e.entry_date}</TableCell>
                    <TableCell className="font-medium">{e.product_code}</TableCell>
                    <TableCell>{e.product_name}</TableCell>
                    <TableCell>{e.category}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(e.types ?? []).map((t) => (
                          <span
                            key={t}
                            className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
                            style={{ borderColor: ACCENT, color: ACCENT }}
                          >
                            {TYPE_LABEL[t] ?? t}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{range}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                          NATURE_PILL[e.creative_nature] ?? NATURE_PILL.others,
                        )}
                      >
                        {NATURE_LABEL[e.creative_nature] ?? e.creative_nature}
                      </span>
                    </TableCell>
                    <TableCell>
                      {e.creative_link ? (
                        <a
                          href={e.creative_link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {e.script_link ? (
                        <a
                          href={e.script_link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        Active
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditing(e);
                              setOpenEntry(true);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => {
                              if (confirm("Delete this entry?")) deleteMut.mutate(e.id);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {openEntry && (
        <EntryModal
          open={openEntry}
          onOpenChange={setOpenEntry}
          editing={editing}
          onSaved={() => qc.invalidateQueries({ queryKey: ["creative-library-entries"] })}
        />
      )}
      {openImport && (
        <ImportModal
          open={openImport}
          onOpenChange={setOpenImport}
          onImported={() => qc.invalidateQueries({ queryKey: ["creative-library-entries"] })}
        />
      )}
    </div>
  );
}

/* -------------------- Entry Modal -------------------- */

function EntryModal({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Entry | null;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const [productCode, setProductCode] = useState(editing?.product_code ?? "");
  const [productName, setProductName] = useState(editing?.product_name ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [rawVideos, setRawVideos] = useState(editing?.raw_videos_link ?? "");
  const [types, setTypes] = useState<string[]>(editing?.types ?? []);
  const [scriptLink, setScriptLink] = useState(editing?.script_link ?? "");
  const [features, setFeatures] = useState(editing?.features_link ?? "");
  const [creativeLink, setCreativeLink] = useState(editing?.creative_link ?? "");
  const [idFrom, setIdFrom] = useState(editing?.creative_id_from ?? "");
  const [idTo, setIdTo] = useState(editing?.creative_id_to ?? "");
  const [nature, setNature] = useState<string>(editing?.creative_nature ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [autofilled, setAutofilled] = useState<Set<string>>(new Set());
  const [autofillNote, setAutofillNote] = useState("");

  const [searchOpen, setSearchOpen] = useState(false);
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!productCode || productCode.length < 1) {
      setResults([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      const { data, error } = await supabase.rpc("search_creative_products" as never, {
        query: productCode,
      } as never);
      if (!error) setResults(((data as unknown) as ProductSearchResult[]) ?? []);
    }, 200);
    return () => {
      if (debRef.current) clearTimeout(debRef.current);
    };
  }, [productCode]);

  const applyProduct = (p: ProductSearchResult) => {
    setProductCode(p.product_code);
    setProductName(p.product_name);
    setCategory(p.category);
    setRawVideos(p.raw_videos_link ?? "");
    setFeatures(p.features_link ?? "");
    setAutofilled(new Set(["product_name", "category", "raw_videos_link", "features_link"]));
    setAutofillNote("✓ 4 fields autofilled from existing product data. Fill remaining fields manually.");
    setSearchOpen(false);
  };

  const toggleType = (t: string) =>
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      // Validate
      if (!productCode.trim()) throw new Error("Product code required");
      if (!productName.trim()) throw new Error("Product name required");
      if (!category) throw new Error("Category required");
      if (!rawVideos.trim()) throw new Error("Raw videos link required");
      if (types.length === 0) throw new Error("Select at least one type");
      if (!scriptLink.trim()) throw new Error("Script link required");
      if (!features.trim()) throw new Error("Features link required");
      if (!creativeLink.trim()) throw new Error("Creative link required");
      if (!nature) throw new Error("Select creative nature");

      // Upsert master product with ignoreDuplicates so ON CONFLICT
      // DO NOTHING is used (no UPDATE permission needed on the shared
      // product master — editors can create rows but not overwrite).
      const { error: upErr } = await supabase
        .from("creative_library_products" as never)
        .upsert(
          {
            product_code: productCode.trim(),
            product_name: productName.trim(),
            category,
            raw_videos_link: rawVideos.trim() || null,
            features_link: features.trim() || null,
          } as never,
          { onConflict: "product_code", ignoreDuplicates: true } as never,
        );
      if (upErr) throw upErr;

      const payload = {
        product_code: productCode.trim(),
        product_name: productName.trim(),
        category,
        raw_videos_link: rawVideos.trim() || null,
        types,
        script_link: scriptLink.trim() || null,
        features_link: features.trim() || null,
        creative_link: creativeLink.trim(),
        creative_id_from: idFrom.trim() || null,
        creative_id_to: idTo.trim() || null,
        creative_nature: nature,
        notes: notes.trim() || null,
      };

      if (editing) {
        const { error } = await supabase
          .from("creative_library_entries" as never)
          .update(payload as never)
          .eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("creative_library_entries" as never)
          .insert({ ...payload, user_id: user.id } as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editing ? "Entry updated" : "Entry saved");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const fieldClass = (k: string) =>
    autofilled.has(k) ? "border-emerald-400 ring-1 ring-emerald-300" : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Creative Entry" : "Add Creative Entry"}</DialogTitle>
          <p className="text-xs text-muted-foreground">Log a new creative for a product</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs">Date</Label>
              <div className="mt-1 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                Today: {todayISTDisplay()} IST
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">Auto-set to today.</p>
            </div>
            <div className="relative">
              <Label className="text-xs">Product Code *</Label>
              <Input
                value={productCode}
                onChange={(e) => {
                  setProductCode(e.target.value);
                  setSearchOpen(true);
                  setAutofilled(new Set());
                  setAutofillNote("");
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="e.g. APNN"
                className="mt-1"
              />
              {searchOpen && results.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover shadow-md">
                  {results.map((r) => (
                    <button
                      key={r.product_code}
                      type="button"
                      onClick={() => applyProduct(r)}
                      className="block w-full border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
                    >
                      <span className="font-bold">{r.product_code}</span>{" "}
                      <span className="text-muted-foreground">· {r.product_name}</span>{" "}
                      <span className="text-xs text-muted-foreground">· {r.category}</span>
                    </button>
                  ))}
                </div>
              )}
              {autofillNote && (
                <p className="mt-1 text-[10px] text-emerald-600">{autofillNote}</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs">
                Product Name *{" "}
                {autofilled.has("product_name") && (
                  <Check className="ml-1 inline h-3 w-3 text-emerald-500" />
                )}
              </Label>
              <Input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className={cn("mt-1", fieldClass("product_name"))}
              />
            </div>
            <div>
              <Label className="text-xs">
                Category *{" "}
                {autofilled.has("category") && (
                  <Check className="ml-1 inline h-3 w-3 text-emerald-500" />
                )}
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className={cn("mt-1", fieldClass("category"))}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3 */}
          <div>
            <Label className="text-xs">
              Raw Videos Link *{" "}
              {autofilled.has("raw_videos_link") && (
                <Check className="ml-1 inline h-3 w-3 text-emerald-500" />
              )}
            </Label>
            <Input
              value={rawVideos}
              onChange={(e) => setRawVideos(e.target.value)}
              placeholder="https://…"
              className={cn("mt-1", fieldClass("raw_videos_link"))}
            />
          </div>

          {/* Row 4: Types */}
          <div>
            <Label className="text-xs">Type *</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {TYPES.map((t) => {
                const on = types.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className="rounded-full border px-3 py-1 text-xs font-medium transition"
                    style={{
                      backgroundColor: on ? ACCENT : "transparent",
                      color: on ? "white" : "inherit",
                      borderColor: on ? ACCENT : undefined,
                    }}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs">Script Link *</Label>
              <Input
                value={scriptLink}
                onChange={(e) => setScriptLink(e.target.value)}
                placeholder="https://…"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">
                Features *{" "}
                {autofilled.has("features_link") && (
                  <Check className="ml-1 inline h-3 w-3 text-emerald-500" />
                )}
              </Label>
              <Input
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                placeholder="https://…"
                className={cn("mt-1", fieldClass("features_link"))}
              />
            </div>
          </div>

          {/* Row 6 */}
          <div>
            <Label className="text-xs">Creative Link *</Label>
            <Input
              value={creativeLink}
              onChange={(e) => setCreativeLink(e.target.value)}
              placeholder="https://…"
              className="mt-1"
            />
          </div>

          {/* Row 7 */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs">Creative ID From</Label>
              <Input
                value={idFrom}
                onChange={(e) => setIdFrom(e.target.value)}
                placeholder="e.g. APNN.456 or APNN.456.V1"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Creative ID To</Label>
              <Input
                value={idTo}
                onChange={(e) => setIdTo(e.target.value)}
                placeholder="e.g. APNN.466 or APNN.466.V2"
                className="mt-1"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Leave blank if single creative
              </p>
            </div>
          </div>

          {/* Row 8: Nature */}
          <div>
            <Label className="text-xs">Creative Nature *</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {NATURES.map((n) => {
                const on = nature === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNature(n)}
                    className="rounded-full border px-3 py-1 text-xs font-medium transition"
                    style={{
                      backgroundColor: on ? ACCENT : "transparent",
                      color: on ? "white" : "inherit",
                      borderColor: on ? ACCENT : undefined,
                    }}
                  >
                    {NATURE_LABEL[n]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any context or references…"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="!justify-between">
          <span className="text-[10px] text-muted-foreground">* Required</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate()}
              style={{ backgroundColor: ACCENT, color: "white" }}
              className="hover:opacity-90"
            >
              {saveMut.isPending ? "Saving…" : "Save Entry →"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Import Modal -------------------- */

const TEMPLATE_HEADERS = [
  "product_code",
  "product_name",
  "category",
  "raw_videos_link",
  "types",
  "script_link",
  "features_link",
  "creative_link",
  "creative_id_from",
  "creative_id_to",
  "creative_nature",
  "notes",
  "date",
];

interface ParsedRow {
  data: Record<string, string>;
  rowNum: number;
  error?: string;
}

function ImportModal({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImported: () => void;
}) {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "creative-library-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }
    setFileName(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const parsed: ParsedRow[] = res.data.map((d, i) => {
          const row: ParsedRow = { data: d, rowNum: i + 2 };
          if (!d.product_code?.trim()) row.error = "Missing product_code";
          else if (!d.creative_link?.trim()) row.error = "Missing creative_link";
          return row;
        });
        setRows(parsed);
      },
      error: (err) => toast.error(err.message),
    });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const validRows = rows.filter((r) => !r.error);
  const invalidRows = rows.filter((r) => r.error);

  const doImport = async () => {
    if (!user) return;
    setImporting(true);
    try {
      const today = todayIST();
      const payload = validRows.map((r) => {
        const d = r.data;
        const types = (d.types || "")
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        return {
          user_id: user.id,
          entry_date: d.date?.trim() || today,
          product_code: d.product_code.trim(),
          product_name: d.product_name?.trim() || d.product_code.trim(),
          category: d.category?.trim() || "Other",
          raw_videos_link: d.raw_videos_link?.trim() || null,
          types,
          script_link: d.script_link?.trim() || null,
          features_link: d.features_link?.trim() || null,
          creative_link: d.creative_link.trim(),
          creative_id_from: d.creative_id_from?.trim() || null,
          creative_id_to: d.creative_id_to?.trim() || null,
          creative_nature: (d.creative_nature?.trim() || "new").toLowerCase(),
          notes: d.notes?.trim() || null,
        };
      });

      // Upsert any new product codes
      const productMap = new Map<string, Record<string, unknown>>();
      payload.forEach((p) => {
        if (!productMap.has(p.product_code)) {
          productMap.set(p.product_code, {
            product_code: p.product_code,
            product_name: p.product_name,
            category: p.category,
            raw_videos_link: p.raw_videos_link,
            features_link: p.features_link,
          });
        }
      });
      if (productMap.size) {
        await supabase
          .from("creative_library_products" as never)
          .upsert(Array.from(productMap.values()) as never, {
            onConflict: "product_code",
            ignoreDuplicates: true,
          } as never);
      }

      const { error } = await supabase
        .from("creative_library_entries" as never)
        .insert(payload as never);
      if (error) throw error;
      toast.success(`${validRows.length} imported, ${invalidRows.length} skipped`);
      onImported();
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Creative Entries</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex items-center gap-2 text-xs">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold"
                style={{
                  backgroundColor: step >= (s as 1 | 2 | 3) ? ACCENT : "#e5e7eb",
                  color: step >= (s as 1 | 2 | 3) ? "white" : "#6b7280",
                }}
              >
                {s}
              </div>
              <span className={cn(step === s ? "font-semibold" : "text-muted-foreground")}>
                {s === 1 ? "Download" : s === 2 ? "Fill" : "Upload"}
              </span>
              {s < 3 && <span className="mx-1 text-muted-foreground">→</span>}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <h3 className="font-semibold">Download the official template</h3>
            <p className="text-sm text-muted-foreground">
              Includes all column headers with instructions in row 1.
            </p>
            <Button
              onClick={downloadTemplate}
              style={{ backgroundColor: ACCENT, color: "white" }}
              className="hover:opacity-90"
            >
              <Download className="mr-1.5 h-4 w-4" /> Download Template CSV
            </Button>
            <div className="rounded-md border bg-muted/30 p-3 text-xs">
              <p>
                <b>Required columns:</b> product_code, creative_link
              </p>
              <p>
                <b>Optional:</b> all other columns
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2 text-sm">
            <h3 className="font-semibold">Fill your CSV using the template</h3>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Product code and creative link are mandatory per row.</li>
              <li>
                <b>types</b> column: comma-separated values e.g. <code>script,music</code>
              </li>
              <li>
                <b>creative_nature</b>: new / variations / hooks / cta / body / others
              </li>
              <li>
                <b>date</b> format: YYYY-MM-DD (leave blank to use today's date)
              </li>
            </ul>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              className="rounded-md border-2 border-dashed p-6 text-center"
            >
              <Upload className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm">Drag & drop your filled CSV here</p>
              <p className="mt-1 text-xs text-muted-foreground">or</p>
              <label className="mt-2 inline-block cursor-pointer text-sm font-medium" style={{ color: ACCENT }}>
                Browse file
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
              </label>
              {fileName && (
                <p className="mt-2 text-xs text-muted-foreground">Loaded: {fileName}</p>
              )}
            </div>

            {rows.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs">
                  Preview (first 5 of {rows.length}) — {validRows.length} valid, {invalidRows.length} invalid
                </p>
                <div className="max-h-40 overflow-auto rounded-md border text-xs">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-2 py-1 text-left">Row</th>
                        <th className="px-2 py-1 text-left">Code</th>
                        <th className="px-2 py-1 text-left">Link</th>
                        <th className="px-2 py-1 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((r) => (
                        <tr key={r.rowNum} className="border-t">
                          <td className="px-2 py-1">{r.rowNum}</td>
                          <td className="px-2 py-1">{r.data.product_code || "—"}</td>
                          <td className="px-2 py-1 truncate">{r.data.creative_link || "—"}</td>
                          <td className="px-2 py-1">
                            {r.error ? (
                              <span className="text-red-600">{r.error}</span>
                            ) : (
                              <span className="text-emerald-600">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="!justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            <X className="mr-1 h-4 w-4" /> Close
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep((step - 1) as 1 | 2)}>
                Back
              </Button>
            )}
            {step < 3 && (
              <Button
                onClick={() => setStep((step + 1) as 2 | 3)}
                style={{ backgroundColor: ACCENT, color: "white" }}
                className="hover:opacity-90"
              >
                Next →
              </Button>
            )}
            {step === 3 && (
              <Button
                disabled={validRows.length === 0 || importing}
                onClick={doImport}
                style={{ backgroundColor: ACCENT, color: "white" }}
                className="hover:opacity-90"
              >
                {importing ? "Importing…" : "Upload & Import →"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
